import { splitTopLevelArgs } from '../stepText'

/**
 * O texto do IFC que a leitura da georreferenciação precisa
 * (`lerConversaoDoMapa`, `lerLocalizacaoDoSitio`), sem pôr o ficheiro
 * inteiro numa cadeia de texto.
 *
 * Porque existe. Quando o IfcMapConversion não aparece nos primeiros
 * 2 MB, o carregador lia o resto do ficheiro, e lia-o todo: uma cadeia
 * do tamanho do IFC. Acima de ~512 MiB isso passa o limite de texto do
 * V8 (no Node é um erro fatal; no browser a georreferenciação perde-se
 * sem aviso, ou a aba rebenta), e abaixo disso custa uma cópia do
 * ficheiro e segundos de expressões regulares sobre centenas de MB.
 *
 * Até LIMITE_DO_TEXTO_INTEIRO o comportamento é o de sempre: o texto
 * todo. Acima, junta-se só o que as leituras usam: as entidades de
 * partida (conversão, CRS, projecto, unidades, sítios, contextos, os
 * ePset de georreferenciação do IFC2X3) e, por referência, tudo o que
 * elas apontam (unidades, colocações, pontos, direcções, propriedades),
 * entidade a entidade, como `#id=TIPO(...);`. As leituras procuram por
 * tipo e por `#id=`, e por isso dão o mesmo resultado sobre este texto
 * e sobre o ficheiro inteiro.
 */

export const LIMITE_DO_TEXTO_INTEIRO = 64 * 1024 * 1024

/** As entidades por onde as leituras começam. */
const TIPOS_DE_PARTIDA = new Set([
  'IFCMAPCONVERSION',
  'IFCMAPCONVERSIONSCALED',
  'IFCPROJECTEDCRS',
  'IFCPROJECT',
  'IFCUNITASSIGNMENT',
  'IFCSITE',
  'IFCBUILDING',
  'IFCGEOMETRICREPRESENTATIONCONTEXT',
])

/** De algumas entidades de partida só interessam alguns argumentos: a
 *  representação de um IfcSite (um terreno, por exemplo) puxava geometria
 *  que nenhuma leitura usa. Índices a partir de 0. */
const SO_ESTES_ARGUMENTOS: Record<string, number[]> = {
  IFCSITE: [5, 13], // ObjectPlacement, SiteAddress (verificacoesGeorref)
  IFCBUILDING: [11], // BuildingAddress (verificacoesGeorref)
  IFCPROJECT: [7, 8], // RepresentationContexts, UnitsInContext
  IFCGEOMETRICREPRESENTATIONCONTEXT: [4, 5], // WorldCoordinateSystem, TrueNorth
}

/** O IfcPropertySet só entra se for um dos ePset de georreferenciação. */
const EPSET = /'epset_(mapconversion|projectedcrs)'/i

/** Quantas voltas de referências se seguem. As cadeias mais longas são as
 *  das unidades por conversão (unidade, medida, unidade, ...). */
const PROFUNDIDADE = 12

export function textoDaGeorreferenciacao(
  bytes: Uint8Array,
  limite = LIMITE_DO_TEXTO_INTEIRO,
): string {
  const decoder = new TextDecoder('latin1')
  if (bytes.length <= limite) return decoder.decode(bytes)
  return textoReduzido(bytes, decoder)
}

const HASH = 0x23 // #
const IGUAL = 0x3d // =
const PLICA = 0x27 // '
const PONTO_E_VIRGULA = 0x3b // ;
const PARENTESE = 0x28 // (

/** Percorre as entidades `#id=TIPO(...);` do ficheiro, sem o descodificar. */
function* entidades(
  bytes: Uint8Array,
): Generator<{ id: number; tipo: string; inicio: number; fim: number }> {
  const n = bytes.length
  let i = 0
  while (i < n) {
    // Início de entidade: '#', dígitos, espaços opcionais, '='.
    if (bytes[i] !== HASH) {
      i += 1
      continue
    }
    let j = i + 1
    let id = 0
    while (j < n && bytes[j] >= 0x30 && bytes[j] <= 0x39) {
      id = id * 10 + (bytes[j] - 0x30)
      j += 1
    }
    while (j < n && (bytes[j] === 0x20 || bytes[j] === 0x09)) j += 1
    if (j === i + 1 || bytes[j] !== IGUAL) {
      i += 1
      continue
    }
    j += 1
    while (j < n && (bytes[j] === 0x20 || bytes[j] === 0x09)) j += 1
    let tipo = ''
    while (j < n && bytes[j] !== PARENTESE && bytes[j] > 0x20) {
      tipo += String.fromCharCode(bytes[j])
      j += 1
    }
    // Fim: o ';' fora de strings.
    let dentro = false
    while (j < n) {
      const c = bytes[j]
      if (c === PLICA) dentro = !dentro
      else if (c === PONTO_E_VIRGULA && !dentro) break
      j += 1
    }
    yield { id, tipo: tipo.toUpperCase(), inicio: i, fim: Math.min(n, j + 1) }
    i = j + 1
  }
}

function referencias(texto: string, tipo: string): number[] {
  const abre = texto.indexOf('(')
  const fecha = texto.lastIndexOf(')')
  if (abre < 0 || fecha <= abre) return []
  const so = SO_ESTES_ARGUMENTOS[tipo]
  const alvo = so
    ? splitTopLevelArgs(texto.slice(abre + 1, fecha))
        .filter((_, k) => so.includes(k))
        .join(',')
    : texto.slice(abre + 1, fecha)
  const out: number[] = []
  for (const m of alvo.matchAll(/#(\d+)/g)) out.push(parseInt(m[1], 10))
  return out
}

function textoReduzido(bytes: Uint8Array, decoder: TextDecoder): string {
  // id -> posição no ficheiro e texto. No fim volta-se à ordem do
  // ficheiro: há leituras que usam "o primeiro" de um tipo.
  const linhas = new Map<number, { inicio: number; texto: string }>()
  let queridos = new Set<number>()

  // 1.ª passagem: as entidades de partida.
  for (const e of entidades(bytes)) {
    const partida = TIPOS_DE_PARTIDA.has(e.tipo)
    if (!partida && e.tipo !== 'IFCPROPERTYSET') continue
    const texto = decoder.decode(bytes.subarray(e.inicio, e.fim))
    if (!partida && !EPSET.test(texto)) continue
    linhas.set(e.id, { inicio: e.inicio, texto })
    for (const r of referencias(texto, e.tipo)) queridos.add(r)
  }

  // Passagens seguintes: o que as já guardadas apontam, até não haver
  // nada de novo ou se chegar à profundidade.
  for (let volta = 0; volta < PROFUNDIDADE; volta += 1) {
    for (const id of linhas.keys()) queridos.delete(id)
    if (queridos.size === 0) break
    const novos = new Set<number>()
    for (const e of entidades(bytes)) {
      if (!queridos.has(e.id) || linhas.has(e.id)) continue
      const texto = decoder.decode(bytes.subarray(e.inicio, e.fim))
      linhas.set(e.id, { inicio: e.inicio, texto })
      for (const r of referencias(texto, e.tipo)) if (!linhas.has(r)) novos.add(r)
    }
    queridos = novos
  }

  return [...linhas.values()]
    .sort((a, b) => a.inicio - b.inicio)
    .map((l) => l.texto)
    .join('\n')
}
